import { useUserSession } from '@solinkcorp/authentication';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { CallhomeApi } from 'clients';
import { usePlaylistCache } from 'components/CacheContext';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import type VideoWallAnalyticsCache from 'components/VideoWallPage/videoWallAnalyticsCache';
import { timeout } from 'promise-tools';
import { useEffect, useMemo } from 'react';
import { useLocalController } from 'solox';
import { type AnalyticsPlatform } from 'src/core/analytics/common';
import { type StreamQuality } from 'src/core/video/types';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import { usePlayListSource } from 'src/hooks/usePlayListSource';
import { uniq } from 'utils/utils';
import { type ThumbnailLevel } from '../components/PlayerThumbnail/VideoThumbnail';
import { PlaybackController } from './PlaybackController';
import { usePauseOnWindowHidden } from './usePauseOnWindowHidden';
import { usePlaybackControllerTick } from './usePlaybackControllerTick';

export const FAST_FAIL_TIMEOUT_IN_MS = 2000;

export function usePlaybackController(
  playingCameras: PlayingCamera[],
  initialPlayingQuality: StreamQuality,
  initialMasterTime: number | (() => number),
  options: {
    thumbnailLevel?: ThumbnailLevel;
    vtt?: boolean;
    quick?: boolean;
    pauseOnHide?: boolean;
    disableLiveModeBarrier?: boolean;
    segmentDurationMs?: number; //optional parameter to be used for reducing segment load, added in for VideoSearch
  } = { pauseOnHide: true },
  videoPlaybackStartsAtDate: Date | undefined = undefined,
  videoPlaybackEndsAtDate: Date | undefined = undefined,
  timezone = getLocalTimeZone(),
  videoPlayerPlatform?: AnalyticsPlatform,
  analyticsCache?: VideoWallAnalyticsCache,
): PlaybackController {
  const playlistCache = usePlaylistCache();
  const playListSource = usePlayListSource();
  const user = useUserSession();

  // Make sure we know which locations are local and which are not.
  const playingDeviceIds = useMemo(
    () => uniq(playingCameras.map((playingCamera) => playingCamera.camera.deviceId)),
    [playingCameras],
  );
  const localLocations = useDevicesAreLocal(playingDeviceIds);

  const playbackController = useLocalController(() => {
    const masterTime =
      typeof initialMasterTime === 'function' ? initialMasterTime() : initialMasterTime;

    return new PlaybackController(
      user,
      playlistCache,
      _checkDeviceOnline,
      playListSource,
      masterTime,
      initialPlayingQuality,
      playingCameras,
      localLocations,
      options,
      videoPlaybackStartsAtDate,
      videoPlaybackEndsAtDate,
      timezone,
      videoPlayerPlatform,
      analyticsCache,
    );
  });

  useEffect(() => {
    playbackController.setUser(user);
  }, [playbackController, user]);

  useEffect(() => {
    playbackController.setPlayingCameras(playingCameras);
  }, [playbackController, playingCameras]);

  useEffect(() => {
    playbackController.setLocalLocations(localLocations);
  }, [playbackController, localLocations]);

  useEffect(() => {
    playbackController.updatePlaylistSource(playListSource);
  }, [playbackController, playListSource]);

  usePlaybackControllerTick(playbackController, { videoPlaybackEndsAtDate });

  usePauseOnWindowHidden(options.pauseOnHide ? playbackController : undefined);

  useEffect(() => {
    return () => playbackController.destroyPressureStateTracker();
  }, [playbackController]);

  return playbackController;
}

// TODO not sure what to do here
export function _checkDeviceOnline(location: AuthLocation): Promise<boolean> {
  return timeout(fetch(`${location.accessibleAddress}/id`), FAST_FAIL_TIMEOUT_IN_MS)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch device ID: ${response.status}`);
      }
      return true;
    })
    .catch(() =>
      // If local fetch fails, check with CH to see if the device is
      // really offline.
      CallhomeApi()
        .isDeviceOffline(location.id)
        .then((result) => !result),
    )
    .catch(() => false);
}
